import {type UrlRule} from "../types.ts";

/**
 * Map the given param value to a different value.
 *
 * This allows you to map a param's value to something else. This does not
 * handle params that have multiple values; if you need that, you will need
 * to create your own rule.
 *
 * @param paramName The name of the param to populate.
 * @param mapper A function that takes the current value of the param and
 * returns the new value. If this returns null, the parameter is removed.
 * @returns A URL rule that sets or deletes the given param when the current
 * param value is mapped using the mapper.
 */
export const mapParam =
    (
        paramName: string,
        mapper: (currentValue: Nullable<string>) => Nullable<string>,
    ): UrlRule =>
    (url) => {
        const paramValue = url.queryParams.get(paramName);
        const mappedValue = mapper(paramValue);
        if (mappedValue == null) {
            url.queryParams.delete(paramName);
        } else {
            url.queryParams.set(paramName, mappedValue);
        }
        return url;
    };
